import React, { useEffect, useState } from "react";
import { getAllGallery } from "../../api/apiHandler";
import { getMediaBaseUrl } from "../../utils/backendUrl";

const ITEMS_PER_LOAD = 20;
const Photos = () => {
  const [photos, setPhotos] = useState([]);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_LOAD);
  const [loading, setLoading] = useState(true);

  const BASE_URL = getMediaBaseUrl();

  useEffect(() => {
    let isMounted = true;
    const loadGallery = async () => {
      try {
        const res = await getAllGallery();
        const items = Array.isArray(res?.data) ? res.data : [];
        if (isMounted) {
          setPhotos(
            items.map((item) => ({
              id: item.id,
              title: item.name || "",
              src: item.image
                ? `${BASE_URL}/uploads/gallery/${encodeURIComponent(item.image)}`
                : "",
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching gallery:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadGallery();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewMore = () => {
    setVisibleCount((prev) => prev + ITEMS_PER_LOAD);
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pb-20">
      
      {/* ================= PREMIUM CATEGORY BANNER ================= */}
      <div className="bg-gradient-to-br from-[#2d006b] to-[#4600a3] text-white py-6 md:py-8 mb-6 relative overflow-hidden shadow-inner">
        {/* Subtle Background Patterns */}
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMCwwLDAsMC4wNSkiLz48L3N2Zz4=')]" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 animate-pulse" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-red opacity-10 rounded-full blur-2xl translate-y-1/3 -translate-x-1/4" />
        
        <div className="max-w-7xl mx-auto px-4 relative z-10 flex flex-col items-center text-center">
          <span className="text-purple-200 text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-1">विशेष तस्वीरें</span>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-black capitalize tracking-tight mb-3 drop-shadow-md">
             फोटो गैलरी
          </h1>
          <div className="w-12 h-1 bg-gradient-to-r from-purple-200 to-white rounded-full opacity-90 shadow-[0_0_15px_rgba(255,255,255,0.5)]" />
        </div>
      </div>

      {/* 🔹 MASONRY GALLERY */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {loading ? (
          <div className="flex flex-col justify-center items-center py-32 gap-4">
             <i className="fa-solid fa-spinner fa-spin text-5xl text-purple-600"></i>
             <p className="text-gray-500 font-medium animate-pulse">गैलरी लोड हो रही है...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {photos.slice(0, visibleCount).map((photo) => (
              <div
                key={photo.id}
                className="group relative rounded-[2rem] overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 aspect-[4/3]"
              >
                <a
                  href={photo.src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full"
                >
                  <img
                    src={photo.src}
                    alt={photo.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-110"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/no-image.png";
                    }}
                  />
                  
                  {/* Overlay Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-in-out" />

                  {/* Fullscreen Icon (Top Right) */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-in-out">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white text-base hover:bg-brand-red transition">
                      <i className="fa-solid fa-expand"></i>
                    </span>
                  </div>

                  {/* Hover Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 translate-y-8 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-in-out">
                    <div className="bg-purple-700 w-8 h-1 mb-3 rounded-full transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 delay-100"></div>
                    <h3 className="text-white text-sm md:text-base font-bold leading-snug line-clamp-2 drop-shadow-md">
                      {photo.title}
                    </h3>
                  </div>
                </a>
              </div>
            ))}
          </div>
        )}

        {/* 🔹 VIEW MORE BUTTON */}
        {!loading && visibleCount < photos.length && (
          <div className="mt-16 flex justify-center pb-12">
            <button
              onClick={handleViewMore}
              className="bg-purple-700 hover:bg-brand-red text-white text-lg font-bold py-4 px-10 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center gap-3"
            >
              और लोड करें <i className="fa-solid fa-arrow-down"></i>
            </button>
          </div>
        )}

        {!loading && photos.length === 0 && (
          <div className="text-center py-32 bg-white rounded-3xl border border-gray-100 shadow-sm mt-8">
             <i className="fa-regular fa-images text-6xl text-gray-300 mb-4"></i>
             <p className="text-gray-500 text-xl font-medium">अभी तक गैलरी में कोई तस्वीर उपलब्ध नहीं है।</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Photos;
